/**
 * @module DocumentoPago
 * @description Componente principal de selección de documentos de pago.
 * Agrupa los documentos pendientes de la persona por servicio de venta en pestañas (Tabs).
 * Cada documento individual se renderiza mediante el sub-componente DocumentoItem.
 *
 * Funcionalidades clave:
 * - Agrupación por tipo de servicio (ej: Crédito, Matrícula, etc.)
 * - Selección de referencia con radio buttons (pago mínimo / total / otro valor)
 * - Cálculo de montos mínimos de abono según configuración del servicio
 * - Bloqueo de referencias con transacciones en proceso
 * - Desmarcado automático de radios de otras referencias al seleccionar una nueva
 *
 * Obtiene todos sus datos del contexto PagosOnline (sin props).
 */

import { FC } from "react";
import { MoneyMask } from "../../helpers";
import {
  Idocumento,
  detallePago,
} from "../../helpers/dataExamples";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import moment from "moment";
import { usePagosOnline } from "./PagosOnlineContext";
import { DocumentoItem } from "./components/DocumentoItem";

const DocumentoPago: FC = () => {
  const {
    documentosPagoDisponibles: documentos,
    estado,
    setEstado,
    serviciosVentasDisponibles: serviciosVentas,
  } = usePagosOnline();

  /**
   * Activa una referencia de pago y configura los detalles asociados al estado.
   * Resetea las referencias previas y calcula valores de abono según el tipo de servicio.
   *
   * Flujo:
   * 1. Desmarca radios de otras referencias (manipulación DOM directa)
   * 2. Crea un nuevo objeto detallePago con los datos del documento
   * 3. Busca el servicio de venta correspondiente para obtener porcentaje de abono y token ePayco
   * 4. Calcula monto mínimo de abono y fecha de vencimiento formateada
   * 5. Actualiza el estado global con la referencia activa y sus propiedades
   *
   * @param doc - Documento de pago seleccionado
   * @param tipoRadio - Modalidad de pago: "min" (mínimo), "tot" (total), "otr" (otro valor)
   */
  function activarReferencia(doc: Idocumento, tipoRadio: string) {
    desmarcarTodoslosRadios(doc.referencia);
    const referencia = doc.referencia;
    const newState = { ...estado };
    newState.referencias = {};
    newState.referenciaActiva = "";

    // Construye el detalle de pago con valores base del documento
    var newReferencia: detallePago = {
      abonable: false,
      descrip: doc.detalle,
      hacer_abono: false,
      limite: doc.vence,
      porMinimoAbono: 0,
      referencia: doc.referencia,
      valorTotal: doc.valorTotal,
      valorMinimo: doc.valorMinimo,
      valorMinimoAbono: 0,
      valor_abono: 0,
      codigoServicio: "",
      codigoServicioSAP: "",
      idServicio: null,
      fechaVencimiento: "",
      tipoPago: tipoRadio,
    };

    const aa = { [referencia]: newReferencia };
    Object.assign(newState.referencias, aa);

    // Busca el servicio de venta que corresponde al código del documento
    let tipoServicio = serviciosVentas.find(
      (srv) => srv.codigoServicio === doc.codigoServicio
    );

    // Calcula fecha de vencimiento formateada
    let vencimiento =
      doc.vence === "00000000"
        ? "No disponible"
        : moment(doc.vence, "DD/MM/YYYY").format("YYYY-MM-DD");

    // Calcula monto mínimo de abono como porcentaje del valor mínimo
    let minimoAbono =
      typeof tipoServicio?.porcentajeAbono != "undefined"
        ? (doc.valorMinimo * tipoServicio?.porcentajeAbono) / 100
        : 0;

    let idServicio =
      typeof tipoServicio?.id != "undefined" ? tipoServicio?.id : "";

    // Actualiza propiedades del servicio en el estado
    newState.sessionId = "";
    newState.idServicio = idServicio;
    newState.referenciaActiva = referencia;
    newState.fechaVencimiento = vencimiento;

    // Enriquece la referencia con datos calculados del servicio
    newState.referencias[referencia].valorMinimoAbono = minimoAbono;
    newState.referencias[referencia].referencia = doc.referencia;
    newState.referencias[referencia].descrip = doc.detalle;
    newState.referencias[referencia].valorTotal = doc.valorTotal;
    newState.referencias[referencia].valorMinimo = doc.valorMinimo;
    newState.referencias[referencia].limite = vencimiento;
    newState.referencias[referencia].abonable =
      typeof tipoServicio?.abonable != "undefined";
    newState.referencias[referencia].codigoServicio = doc.codigoServicio;
    newState.referencias[referencia].codigoServicioSAP =
      typeof tipoServicio?.codigoServicioSap != "undefined"
        ? tipoServicio?.codigoServicioSap
        : "";
    newState.referencias[referencia].fechaVencimiento = vencimiento;

    setEstado(newState);
  }

  /**
   * Maneja el cambio de valor en el campo de abono personalizado ("Otro valor").
   * Limpia caracteres no numéricos y actualiza el estado de abono de la referencia.
   *
   * @param event - Evento de cambio del input
   * @param referencia - Identificador de la referencia de pago
   */
  const eventoRealizarAbono = (
    event: React.ChangeEvent<HTMLInputElement>,
    referencia: string
  ) => {
    const newState = { ...estado };
    let valor: number = parseInt(event.target.value.replace(/\D/g, ""));
    valor = Number.isNaN(valor) ? 0 : valor;
    newState.referencias[referencia].valor_abono = valor;
    newState.referencias[referencia].hacer_abono = valor === 0 ? false : true;
    setEstado(newState);
  };

  /**
   * Desmarca todos los radio buttons del DOM excepto los de la referencia especificada.
   * Necesario porque los radios están en diferentes DocumentoItems sin compartir un name group.
   * Usa manipulación directa del DOM por limitaciones del patrón de radio buttons no agrupados.
   *
   * @param referencia - Referencia cuyos radio buttons NO deben desmarcarse
   */
  const desmarcarTodoslosRadios = (referencia: string) => {
    const nodes: NodeListOf<HTMLInputElement> = document.querySelectorAll(
      'input[type="radio"]:not([name="tipo' + referencia + '"])'
    );
    nodes.forEach((v, k) => {
      v.checked = false;
    });
  };

  /**
   * Filtra los servicios de venta que tienen al menos un documento asociado.
   * Solo estos servicios generarán pestañas (Tabs) en la interfaz.
   */
  let serviciosVentaPersona = serviciosVentas.filter((srv) =>
    documentos.some((doc) => doc.codigoServicio === srv.codigoServicio)
  );

  return (
    <>
      <div className="row mt-6 principal-documentosPago">
        <div className="col">
          {documentos.length === 0 ? (
            /* Mensaje cuando no hay documentos pendientes de pago */
            <p style={{ textAlign: "center" }}>
              No hay documentos disponibles para pago
            </p>
          ) : (
            <div>
              <Tabs>
                <TabList>
                  {/* Pestañas: una por cada servicio de venta con documentos */}
                  {serviciosVentaPersona.map((srv, index) => {
                    return (
                      <Tab key={"Key-Tab-" + index}>
                        <p>{srv.nombre}</p>
                      </Tab>
                    );
                  })}
                </TabList>

                {/* Paneles: lista de documentos filtrados por servicio */}
                {serviciosVentaPersona.map((srv2, index2) => {
                  return (
                    <TabPanel
                      key={"Key-TabPanel-" + index2}
                      className={"container_documentos_pagos"}
                    >
                      {documentos.map((doc, index3) => {
                        // Filtra documentos que no pertenecen al servicio de esta pestaña
                        if (srv2.codigoServicio !== doc.codigoServicio) {
                          return false;
                        }

                        // Busca el tipo de servicio para propiedades de abono
                        let tipoServicio = serviciosVentas.find(
                          (srv) =>
                            srv.codigoServicio === doc.codigoServicio
                        );

                        // Pre-computa valores para DocumentoItem
                        let checked =
                          estado.referenciaActiva === doc.referencia;
                        let abonable = tipoServicio?.abonable;
                        let minimoAbono =
                          typeof tipoServicio?.porcentajeAbono != "undefined"
                            ? (doc.valorMinimo *
                              tipoServicio?.porcentajeAbono) /
                            100
                            : 0;
                        minimoAbono = Math.ceil(minimoAbono);
                        let moneyPagoTotal = MoneyMask(doc.valorTotal + "");
                        let moneyPagoMinimo = MoneyMask(doc.valorMinimo + "");
                        let vencimiento =
                          doc.vence === "00000000"
                            ? "No disp."
                            : moment(doc.vence, "DD/MM/YYYY").format(
                              "YYYY-MM-DD"
                            );
                        let tipoPagoRef =
                          typeof estado.referencias[doc.referencia] ===
                          "undefined"
                            ? ""
                            : estado.referencias[doc.referencia].tipoPago;

                        return (
                          <DocumentoItem
                            key={index3}
                            doc={doc}
                            index={index3}
                            checked={checked}
                            abonable={abonable}
                            minimoAbono={minimoAbono}
                            moneyPagoTotal={moneyPagoTotal}
                            moneyPagoMinimo={moneyPagoMinimo}
                            vencimiento={vencimiento}
                            tipoPagoRef={tipoPagoRef}
                            onActivarReferencia={activarReferencia}
                            onRealizarAbono={eventoRealizarAbono}
                          />
                        );
                      })}
                    </TabPanel>
                  );
                })}
              </Tabs>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export { DocumentoPago };
